import { IHeartbeatMonitor } from "./interfaces";
import { sessionStore } from "@auvious/utils";
import { ErrorHandler } from "../error.handler.service";
import ky from "ky";
import { AppConfigService } from "../app.config.service";
import { IConversation } from "@auvious/integrations";

const GENESYS_JWT_TOKEN = "auvious.genesys.jwt";

export class GenesysWebChatHeartbeatMonitor implements IHeartbeatMonitor {
  private _jwtToken;
  private _id;
  private _retryCount = 3;

  public async createMonitor(
    conversation: IConversation,
    timeoutSeconds: number
  ): Promise<void> {
    // on refresh, we reconnect but we don't get back the jwt token from genesys.
    if (conversation.jwt) {
      sessionStore.setItem(GENESYS_JWT_TOKEN, conversation.jwt);
      this._jwtToken = conversation.jwt;
    } else {
      this._jwtToken = sessionStore.getItem(GENESYS_JWT_TOKEN);
    }

    if (!this._jwtToken) {
      return;
    }

    this._id = conversation.id;

    try {
      await this.retryRequest(
        conversation.id,
        conversation.memberId,
        timeoutSeconds,
        this._retryCount,
        100
      ); // Retry after 500 milliseconds
    } catch (ex) {
      ErrorHandler.captureError(ex);
      throw ex;
    }
  }

  private async retryRequest(
    interactionId: string,
    memberId: string,
    timeoutSeconds: number,
    retriesLeft: number,
    delay: number
  ): Promise<void> {
    try {
      await ky
        .post(
          `${AppConfigService.auviousBaseUrl}/api/resources/external/genesys/webchat`,
          {
            json: {
              conversationId: interactionId,
              jwt: this._jwtToken,
              memberId,
              pcEnvironment: AppConfigService.pcEnvironment,
              timeoutSeconds,
            },
          }
        )
        .json();
    } catch (ex) {
      if (
        retriesLeft > 0 &&
        ex.response &&
        ex.response.status >= 500 &&
        ex.response.status < 600
      ) {
        await new Promise((resolve) => setTimeout(resolve, delay)); // Wait before retrying
        // Increase delay for next retry
        const nextDelay = delay * 2;
        // Retry the request recursively with one less retry count and increased delay
        await this.retryRequest(
          interactionId,
          memberId,
          timeoutSeconds,
          retriesLeft - 1,
          nextDelay
        );
      } else {
        throw ex;
      }
    }
  }

  public cleanup() {
    this._jwtToken = undefined;
    this._id = undefined;
  }

  public async destroyMonitor() {
    try {
      // remove token from store
      sessionStore.removeItem(GENESYS_JWT_TOKEN);

      if (!this._id) {
        return;
      }

      // notify to stop
      await ky.delete(
        `${AppConfigService.auviousBaseUrl}/api/resources/external/genesys/webchat/${this._id}`,
        {
          headers: {
            Authorization: `Bearer ${this._jwtToken}`,
          },
        }
      );
    } catch (ex) {
      ErrorHandler.captureError(ex);
      throw ex;
    }
  }

  async beat() {
    try {
      await ky.post(
        `${AppConfigService.auviousBaseUrl}/api/resources/external/genesys/webchat/${this._id}/heartbeat`,
        {
          headers: {
            Authorization: `Bearer ${this._jwtToken}`,
          },
        }
      );
    } catch (ex) {
      ErrorHandler.captureError(ex);
      throw ex;
    }
  }
}
